import {
  HOST,
  type AnchorSpec,
  sidebarAnchor,
  sidebarCardAnchor,
  submenuAnchor,
} from './engine/anchors';

export interface TourStep {
  id: string;
  title: string;
  description: string;
  navLabel?: string;
  anchor: AnchorSpec;
  side?: 'top' | 'right' | 'bottom' | 'left' | 'over';
  group?: number;
  parentStepId?: string;
  requiresModule?: string;
  lockTarget?: boolean;
}

export interface TourStage {
  id: string;
  label: string;
  stepIds: string[];
}

export interface Tour {
  id: string;
  label: string;
  description: string;
  stages: TourStage[];
  steps: TourStep[];
  order?: 'authored' | 'dom';
}

function moduleStep(
  id: string,
  label: string | string[],
  title: string,
  description: string,
): TourStep {
  return {
    id,
    title,
    description,
    navLabel: Array.isArray(label) ? label[0] : label,
    anchor: sidebarAnchor(label, `nav-${id}`),
    side: 'right',
  };
}

type SubmoduleSpec = [label: string | string[], description: string, title?: string];

function submodules(
  parentStepId: string,
  moduleLabel: string | string[],
  items: SubmoduleSpec[],
): TourStep[] {
  const labels = Array.isArray(moduleLabel) ? moduleLabel : [moduleLabel];
  const moduleName = labels[0];

  return items.map(([label, description, title]) => {
    const subName = Array.isArray(label) ? label[0] : label;
    return {
      id: `${parentStepId}--${subName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`,
      title: title ?? `${moduleName} › ${subName}`,
      description,
      navLabel: subName,
      anchor: submenuAnchor(labels, label),
      side: 'right',
      parentStepId,
      requiresModule: moduleName,
    };
  });
}

const MODULE_STEPS: TourStep[] = [
  moduleStep(
    'overview',
    'Overview',
    'Overview — your starting point',
    'The home dashboard. It summarises the whole workspace at a glance: how many conversations came in, what your campaigns delivered, how leads are moving through the pipeline, and where revenue is coming from. Use it to spot what needs attention, then jump straight into that module.',
  ),
  moduleStep(
    'inbox',
    'Inbox',
    'Inbox — every conversation in one place',
    'A shared team inbox for WhatsApp, Instagram and Facebook. Reply, send templates and media, assign a chat to a teammate, set its priority, and filter by unread or assignee. Everything a customer has ever said to you lives on one thread.',
  ),
  moduleStep(
    'new-campaigns',
    ['Campaigns', 'New Campaigns'],
    'Campaigns — reach many people at once',
    'Build and send WhatsApp broadcasts, and write the approved templates they go out on — one screen with two tabs: Campaigns for the sends and Templates for the wording behind them. Pick an audience, schedule the send, then watch delivery, reads and replies land per campaign; replies come back into the Inbox as ordinary conversations. Try it below and the walkthrough takes one tab at a time: it rings Campaigns and offers to build a send, then rings Templates and offers to write the wording behind it.',
  ),
  moduleStep(
    'crm',
    'CRM',
    'CRM — leads, deals and tickets',
    'Your customer database as a drag-and-drop board. Move leads through pipeline stages, record deal values, log notes and activities, and raise support tickets. Every contact links back to their conversation in the Inbox.',
  ),
  moduleStep(
    'automations',
    'Automations',
    'Automations — let the system reply for you',
    'A visual flow builder. Trigger on a keyword, a form submission, an abandoned cart or a schedule, then chain actions: send a message, wait, branch on a condition, collect a payment, or hand off to a human. Forms and their submissions are here as well.',
  ),
  moduleStep(
    'social-media',
    'Social Media',
    'Social Media — publish and schedule posts',
    'Create posts once and publish them to Instagram, Facebook, LinkedIn and YouTube. Schedule ahead on a calendar, review what is queued, and see how each post performed after it went out.',
  ),
  moduleStep(
    'instagram',
    'Instagram',
    'Instagram — comments, DMs and insights',
    'Instagram-specific automation: reply to comments on a post, send the commenter a DM, and keep the conversation going with AI grounded in that product. Post performance and account insights sit alongside it.',
  ),
  moduleStep(
    'appointments',
    'Appointments',
    'Appointments — let customers book time',
    'Publish a booking link with your real availability. Customers pick a slot, get a confirmation and a calendar invite, and receive WhatsApp reminders before the meeting. Supports one-to-one slots and fixed-time group sessions.',
  ),
  moduleStep(
    'commerce',
    'Commerce',
    'Commerce — catalogue, orders and payments',
    'Sell without leaving chat. Sync a product catalogue from Shopify or WooCommerce, share products in a conversation, take payments through a link, and track every order from placed to delivered.',
  ),
  moduleStep(
    'ads',
    'Ads',
    'Ads — Meta campaigns and attribution',
    'Connect a Meta ad account to run and measure click-to-WhatsApp and lead-form ads. See spend, cost per lead, and — because leads land in your CRM — which ads produced customers rather than just clicks.',
  ),
  moduleStep(
    'reports',
    'Reports',
    'Reports — measure what happened',
    'Analytics across the whole workspace: message volume and response times, campaign performance, pipeline conversion, and revenue. Filter by date range and export what you need.',
  ),
  moduleStep(
    'ai',
    ['AI Agents', 'AI'],
    'AI Agents — agents that work for you',
    'Configure AI agents that answer customer questions from your own knowledge base, qualify leads, and take over routine replies — handing off to a person the moment a conversation needs one.',
  ),
  moduleStep(
    'petpooja',
    'Petpooja',
    'Petpooja — connect your restaurant POS',
    'Links a Petpooja point-of-sale to this workspace. Your menu and store open/closed status sync across, orders placed in chat are pushed straight into the POS, and status changes flow back to the customer on WhatsApp automatically.',
  ),
];


const SUBMODULE_STEPS: TourStep[] = [
  ...submodules('inbox', 'Inbox', [
    [
      'WhatsApp',
      'Every WhatsApp thread for this business account in one shared queue. Reply, send an approved template, attach media, assign the chat to a teammate or set its priority — and the 24-hour messaging window is tracked for you, so you know when a template is required instead of finding out from a failed send.',
    ],
    [
      'Instagram',
      'Instagram DMs and comment-led conversations in the same inbox. A thread opened by a comment remembers the post it came from, so replies stay grounded in the right product rather than the last thing discussed.',
    ],
    [
      'Facebook',
      'Messages from your Facebook Page, alongside everything else. Same reply tools, same assignment and priority, so nobody has to watch a second tab.',
    ],
    [
      'AI Chats',
      'Conversations your AI agents are handling on their own. Read what was said, see the point where the AI handed off to a person, and step in mid-thread whenever you want to take it over yourself.',
    ],
  ]),


  ...submodules('automations', 'Automations', [
    [
      'Automations',
      'The flow list and the visual builder. Start from a keyword, a form submission, an abandoned cart or a schedule, then chain messages, waits, conditions, payments and a handoff to a human.',
      'Automations › Flows',
    ],
    [
      'Forms',
      'Forms you can share as a link or drop into a flow. Submissions land in the CRM as leads and can start an automation the moment they arrive.',
    ],
  ]),

  ...submodules('appointments', 'Appointments', [
    [
      'Overview',
      'Booking activity at a glance — what is coming up, what was booked recently, and how full the calendar is looking.',
    ],
    [
      'Scheduling',
      'Your event types: the kinds of meeting you publish a link for. Set the duration, the fee, how much notice you need, and whether it happens on Zoom, Google Meet, a phone call or in person.',
    ],
    [
      'Meetings',
      'Every booking that has been made, with the invitee, the join link and the option to cancel or reschedule. Cancelling here notifies the invitee for you.',
    ],
    [
      'Contacts',
      'The people who have booked with you, linked back to their CRM record and their conversation history.',
    ],
    [
      'Availability Slots',
      'Your weekly working hours, plus any recurring breaks you name — lunch, a standup, the school run. Anything outside these is never offered to an invitee, so you cannot be booked over your own day.',
    ],
    [
      'Calendar Integrations',
      'Connect Google Calendar, Outlook or Zoom. Once connected, a booking creates a real calendar event and a real meeting link on your own account rather than a placeholder.',
    ],
    [
      'WhatsApp Reminders',
      'Reminders before a meeting, sent on WhatsApp rather than only by email — which is the difference between a booking and someone actually turning up.',
    ],
  ]),

  ...submodules('commerce', 'Commerce', [
    [
      'Overview',
      'Selling at a glance: recent orders, what has been paid for, and what the catalogue is doing.',
    ],
    [
      'Product Catalog',
      'Products synced from Shopify or WooCommerce, or added by hand. Any of them can be shared straight into a conversation for the customer to buy from.',
    ],
    [
      'Orders',
      'Every order from placed to delivered, whether it started in a chat, in an automation or in your store.',
    ],
    [
      'Abandoned Carts',
      'Carts that were started and never paid for. Pair this with an automation and the reminder goes out on its own, once per cart.',
    ],
    [
      'Payments',
      'Payment links you have sent and what became of them, across whichever gateways you have connected.',
    ],
    [
      'Invoices',
      'Invoices raised against orders, showing what has been paid and what is still outstanding.',
    ],
  ]),

  ...submodules('ads', 'Ads', [
    [
      'Meta Ads',
      'The Meta ad account itself: campaigns, ad sets and ads with spend, cost per lead and the CRM outcome of every lead they produced — plus breakdowns by age, placement and region, spend pacing against budget, and creative comparison.',
    ],
    [
      'Google Ads',
      'Google Ads performance here too, so cost per lead can be compared across every channel you buy rather than one report per platform.',
    ],
  ]),

  ...submodules('petpooja', 'Petpooja', [
    [
      'Overview & Sync',
      'The connection itself: push your menu and store open or closed status across, and check when each last synced.',
      'Petpooja › Overview and sync',
    ],
    [
      'Order History',
      'Orders that moved between WhatsApp and the POS, including what the POS said back about each one.',
    ],
  ]),


];

const WORKSPACE_STEPS: TourStep[] = [
  {
    id: 'workspaces',
    navLabel: 'Workspaces',
    title: 'Workspaces — which business you are working in',
    description:
      'Every screen in this product is scoped to one workspace, and this is where you pick it. A workspace is one connected WhatsApp Business number with its own conversations, contacts, campaigns and reports — so switching here changes what the whole app is showing you, not just this page. Businesses running a sales line and a support line keep them apart this way. Try it below and the walkthrough opens the switcher, shows you which workspace you are in, where a new one gets added, and where Integrations for this workspace lives.',
    anchor: { selector: HOST.workspaceSwitcher },
    side: 'right',
    lockTarget: true,
  },
];

const ACCOUNT_STEPS: TourStep[] = [
  {
    id: 'account-menu',
    group: 2,
    navLabel: 'Account menu',
    title: 'Account menu — settings, your team and sign out',
    description:
      'Your own profile control, and the way into everything about you rather than the workspace. Account settings holds your business details, password and security, notifications, your plan and your invoices. WhatsApp Business accounts, MCP and AI Knowledge live on Workspace settings. Beside it: Team management for inviting teammates and choosing exactly which sections each of them can open, the Audit log — a record of who changed what, when, and from where — and Data Store, the spreadsheet-style tables you define yourself. Guide and Updates are here too, and Sign Out at the bottom. Try it below and the walkthrough opens the menu and takes one item at a time.',
    anchor: { selector: HOST.accountMenuTrigger },
    side: 'bottom',
    lockTarget: true,
  },
];

const NEXA_STEPS: TourStep[] = [
  {
    id: 'nexa-genui',
    group: 1,
    navLabel: 'Nexa AI workspace',
    title: 'Nexa AI workspace — the AI builds the screen',
    description:
      'Opens a full-page AI workspace. Describe what you want in plain language — "show me last week\'s campaign results", "build a flow that chases abandoned carts" — and Nexa assembles the interface and performs the action for you, instead of you hunting for the right page. The toggle switches between the workspace and the normal app.',
    anchor: sidebarCardAnchor(['Nexa AI', 'Nexa'], 'nexa-genui-card', HOST.nexaCardIcon),
    side: 'right',
  },
  {
    id: 'nexa-ask-ai',
    group: 1,
    navLabel: 'Ask AI',
    title: 'Ask AI — quick answers and shortcuts',
    description:
      'A chat panel for fast guidance without leaving the page you are on. Ask where something lives ("where can I manage contacts?"), what a setting does, or how to get something done, and it answers and can take you straight there. Use the workspace to do the work; use this to find your way around.',
    anchor: { selector: HOST.headerAiLauncher, node: 'nexa-ask-ai-launcher' },
    side: 'bottom',
  },
];

const PRODUCT_TOUR_STEPS: TourStep[] = [
  ...WORKSPACE_STEPS,
  ...MODULE_STEPS,
  ...SUBMODULE_STEPS,
  ...NEXA_STEPS,
  ...ACCOUNT_STEPS,
];

export const PRODUCT_TOUR: Tour = {
  id: 'product-tour',
  label: 'Product tour',
  description:
    'A guided walk through the whole workspace — which business you are in, every module in the sidebar, then where to set it all up.',
  order: 'dom',
  steps: PRODUCT_TOUR_STEPS,
  stages: [
    { id: 'workspaces', label: 'Workspaces', stepIds: ['workspaces'] },
    { id: 'start', label: 'Start here', stepIds: ['overview'] },
    {
      id: 'engage',
      label: 'Talk to customers',
      stepIds: ['inbox', 'new-campaigns'],
    },
    { id: 'organise', label: 'Organise & automate', stepIds: ['crm', 'automations'] },
    { id: 'social', label: 'Social Media', stepIds: ['social-media'] },
    { id: 'instagram', label: 'Instagram', stepIds: ['instagram'] },
    { id: 'operate', label: 'Book & sell', stepIds: ['appointments', 'commerce'] },
    { id: 'measure', label: 'Measure & scale', stepIds: ['ads', 'reports'] },
    { id: 'ai', label: 'AI Agents', stepIds: ['ai'] },
    { id: 'petpooja', label: 'Petpooja', stepIds: ['petpooja'] },
    { id: 'nexa', label: 'Nexa AI', stepIds: ['nexa-genui', 'nexa-ask-ai'] },
    { id: 'account', label: 'Account menu', stepIds: ['account-menu'] },
  ],
};


export const WORKSPACES_TOUR_ID = 'workspaces-intro';

export const WORKSPACES_INTRO_STEP_ID = 'ws-intro';

const WORKSPACE_LIST: AnchorSpec = { selector: '[data-testid="sidebar-workspaces-section"]' };
const CREATE_ROW: AnchorSpec = { selector: '[data-testid="sidebar-create-workspace"]' };
const MANAGE_ROW: AnchorSpec = { selector: '[data-testid="sidebar-manage-workspaces"]' };
const SETTINGS_ROW: AnchorSpec = { selector: '[data-testid="sidebar-workspace-settings"]' };
const INTEGRATIONS_ROW: AnchorSpec = { selector: '[data-testid="sidebar-integrations"]' };

const WORKSPACES_INTRO_STEPS: TourStep[] = [
  {
    id: WORKSPACES_INTRO_STEP_ID,
    navLabel: 'What changed',
    title: 'Your account now has workspaces',
    description:
      'A workspace holds one business: its WhatsApp, Instagram, Facebook, LinkedIn, TikTok and YouTube channels, plus its inbox, contacts and reports. Anything you already connected is exactly where you left it — nothing moved, nothing was deleted.',
    anchor: { selector: HOST.workspaceSwitcher },
    side: 'right',
    lockTarget: true,
  },
  {
    id: 'ws-current',
    navLabel: 'Your workspaces',
    title: 'Everything you can open',
    description:
      'The tick marks where you are. Click another row and the whole app follows it — inbox, CRM, campaigns, social posts, ads, reports. Only workspaces you belong to appear here.',
    anchor: WORKSPACE_LIST,
    side: 'right',
    lockTarget: true,
  },
  {
    id: 'ws-create',
    navLabel: 'Add one',
    title: 'Start a new one',
    description:
      'Creates a fresh, empty workspace. Connect WhatsApp, Instagram, Facebook or any other channel whenever you are ready — it does not need one to exist.',
    anchor: CREATE_ROW,
    side: 'right',
    lockTarget: true,
  },
  {
    id: 'ws-manage',
    navLabel: 'Manage',
    title: 'See them all at once',
    description:
      'Rename a workspace, check who is in it, and pick which one opens when you sign in.',
    anchor: MANAGE_ROW,
    side: 'right',
    lockTarget: true,
  },
  {
    id: 'ws-settings',
    navLabel: 'Settings',
    title: 'Settings live per workspace',
    description:
      'Profile, branding, AI knowledge and MCP belong to the workspace above — not to your account. Switch, and you are editing a different set.',
    anchor: SETTINGS_ROW,
    side: 'right',
    lockTarget: true,
  },
  {
    id: 'ws-integrations',
    navLabel: 'Integrations',
    title: 'Connections are per workspace too',
    description:
      'WhatsApp, Instagram, Facebook, LinkedIn, TikTok, YouTube, your Shopify or WooCommerce store, your Meta and Google ad accounts. Each belongs to this workspace alone, so two can hold entirely different sets.',
    anchor: INTEGRATIONS_ROW,
    side: 'right',
    lockTarget: true,
  },
];

export function workspacesIntroDescription(connectedNumbers: number): string {
  return connectedNumbers >= 2
    ? 'Each WhatsApp number you had connected became its own workspace. Your Instagram, Facebook, LinkedIn and other channels, and everything inside them, are exactly where you left them — nothing moved, nothing was deleted.'
    : 'Your account now sits inside a workspace — its WhatsApp, Instagram, Facebook, LinkedIn and other channels, with its inbox, contacts and reports, exactly where you left them. Nothing moved, nothing was deleted. Add another whenever you want to keep a separate line of business apart.';
}

export interface BuildWorkspacesTourOptions {
  connectedNumbers?: number;
}

export function buildWorkspacesTour(options: BuildWorkspacesTourOptions = {}): Tour {
  const { connectedNumbers = 0 } = options;

  const steps: TourStep[] = WORKSPACES_INTRO_STEPS.map((step) =>
    step.id === WORKSPACES_INTRO_STEP_ID
      ? { ...step, description: workspacesIntroDescription(connectedNumbers) }
      : step,
  );

  return {
    id: WORKSPACES_TOUR_ID,
    label: 'Workspaces',
    description: 'What workspaces are, and what happened to everything you already had.',
    order: 'authored',
    steps,
    stages: [
      {
        id: 'workspaces-intro',
        label: 'Workspaces',
        stepIds: steps.map((s) => s.id),
      },
    ],
  };
}

export const TOURS: Tour[] = [PRODUCT_TOUR];

export function getTour(id: string): Tour | undefined {
  return TOURS.find((t) => t.id === id);
}

export type StageState = 'pending' | 'active' | 'complete';

export interface StepProgress {
  id: string;
  label: string;
  state: StageState;
  depth: number;
}

export interface StageProgress {
  id: string;
  label: string;
  state: StageState;
  entryStepId: string;
  stepCount: number;
  steps: StepProgress[];
}

export interface TourProgressView {
  stages: StageProgress[];
  activeStage: number;
  totalStages: number;
}

export function computeStageProgress(
  tour: Tour,
  order: string[],
  currentStepId: string | null,
): TourProgressView {
  const positionOf = new Map(order.map((id, i) => [id, i]));
  const current = currentStepId !== null ? positionOf.get(currentStepId) ?? -1 : -1;

  const stepById = new Map(tour.steps.map((s) => [s.id, s]));
  const directStage = new Map<string, string>();
  for (const stage of tour.stages) {
    for (const id of stage.stepIds) directStage.set(id, stage.id);
  }

  const stageIdOf = (stepId: string): string | undefined => {
    let cursor: string | undefined = stepId;
    for (let hops = 0; cursor !== undefined && hops < 8; hops++) {
      const direct = directStage.get(cursor);
      if (direct) return direct;
      cursor = stepById.get(cursor)?.parentStepId;
    }
    return undefined;
  };

  const positionsByStage = new Map<string, number[]>();
  order.forEach((stepId, i) => {
    const stageId = stageIdOf(stepId);
    if (!stageId) return;
    const list = positionsByStage.get(stageId);
    if (list) list.push(i);
    else positionsByStage.set(stageId, [i]);
  });

  const stages: StageProgress[] = [];
  for (const stage of tour.stages) {
    const positions = positionsByStage.get(stage.id);
    if (!positions || positions.length === 0) continue;

    const first = positions[0];
    const last = positions[positions.length - 1];
    let state: StageState = 'pending';
    if (current >= first && current <= last) state = 'active';
    else if (current > last) state = 'complete';

    stages.push({
      id: stage.id,
      label: stage.label,
      state,
      entryStepId: order[first],
      stepCount: positions.length,
      steps: positions.map((position) => {
        const stepId = order[position];
        const step = stepById.get(stepId);
        return {
          id: stepId,
          label: step?.navLabel ?? step?.title ?? stepId,
          state:
            position === current ? 'active' : position < current ? 'complete' : ('pending' as StageState),
          depth: step?.parentStepId ? 1 : 0,
        };
      }),
    });
  }

  const activeStage = stages.findIndex((s) => s.state === 'active') + 1;
  return { stages, activeStage, totalStages: stages.length };
}

export function validateTours(tours: Tour[] = TOURS): string[] {
  const problems: string[] = [];

  for (const tour of tours) {
    const ids = new Set<string>();
    for (const step of tour.steps) {
      if (ids.has(step.id)) problems.push(`${tour.id}: duplicate step id "${step.id}"`);
      ids.add(step.id);
    }

    const staged = new Set(tour.stages.flatMap((s) => s.stepIds));
    for (const step of tour.steps) {
      if (step.parentStepId) {
        const parent = tour.steps.find((s) => s.id === step.parentStepId);
        if (!parent) {
          problems.push(
            `${tour.id}: submodule step "${step.id}" names a missing parent "${step.parentStepId}"`,
          );
        } else if (parent.parentStepId) {
          problems.push(
            `${tour.id}: submodule step "${step.id}" is nested under another submodule ` +
              `("${parent.id}") — the engine expands one level only`,
          );
        }
        if (!step.requiresModule) {
          problems.push(
            `${tour.id}: submodule step "${step.id}" has no requiresModule, so the engine ` +
              `cannot open the group it lives in`,
          );
        }
        if (staged.has(step.id)) {
          problems.push(
            `${tour.id}: submodule step "${step.id}" is listed in a stage; children inherit ` +
              `their parent's stage and must not be named again`,
          );
        }
        continue;
      }
      if (!staged.has(step.id)) problems.push(`${tour.id}: step "${step.id}" is in no stage`);
    }
    for (const stage of tour.stages) {
      for (const stepId of stage.stepIds) {
        if (!ids.has(stepId)) {
          problems.push(`${tour.id}: stage "${stage.id}" references missing step "${stepId}"`);
        }
      }
    }
  }

  return problems;
}
